import React from 'react';
import { Button, Modal, Form, SelectHierarchyLabel, ToggleLabel, Section, TooltipHelp, ToastContext } from '@weezevent/nacre';
import { requiredField, useForm } from '../../../utils/validators';
import { useTranslation } from 'react-i18next';
import RatesTreeModel from '../../../models/ratesTreeModel';
import SwappableRateConfig from '../../../models/swappableRateConfig';
import RateFeesEditor from '../../../components/rates/rate_fees/rateFeesEditor';

export default function ModalSwappableConfigRule({ organization, show, onClose, event, rate, swappableConfigs, syncSwappableConfig, configToEdit, loadingSwappableConfig }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const [displayFees, setDisplayFees] = React.useState(configToEdit && typeof configToEdit === 'object' && configToEdit.fees.length ? true : false);

    const getInitialFormValues = React.useMemo(() => {
        if (configToEdit && typeof configToEdit === 'object') {
            return {
                id: configToEdit.id,
                name: configToEdit.name,
                rate_from: configToEdit.rate_from,
                rates_to: configToEdit.rates_to,
                pay_positive_difference: configToEdit.pay_positive_difference,
                refund_positive_difference: configToEdit.refund_positive_difference,
                fees: configToEdit.fees
            };
        }
        return {
            id: null,
            name: rate.name,
            rate_from: rate.id,
            rates_to: [],
            pay_positive_difference: true,
            refund_positive_difference: false,
            fees: []
        };
    }, [configToEdit, rate]);

    const validators = React.useMemo(() => {
        return {
            name: requiredField
        };
    }, []);

    const [form, setForm, , formErrors] = useForm(getInitialFormValues, validators);

    const [ratesTree, loadingRates] = RatesTreeModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: event.id,
            cache: false,
            allow_cache: false
        },
        [organization, event]
    );

    const allRatesToIds = React.useMemo(() => {
        if (!swappableConfigs.length) return [];

        // Get all rates_to IDs
        const allIds = swappableConfigs.flatMap(config => config.rates_to);

        // Filter only if configToEdit exists and is not true
        if (configToEdit && configToEdit !== true) {
            return allIds.filter(id => !configToEdit.rates_to.includes(id));
        }

        return allIds;
    }, [swappableConfigs, configToEdit]);

    const ratesOptions = React.useMemo(() => {
        if (!ratesTree) return;

        const _ratesOptions = ratesTree.toOptions({
            filter: rate => !allRatesToIds.includes(rate.pk) && !rate.distributor && rate.pk !== form.rate_from
        });
        return _ratesOptions;
    }, [form, ratesTree, allRatesToIds]);

    const handleLabelChange = React.useCallback(selected => {
        return selected.length
            ? `${selected.length} ${
                  selected.length > 1
                      ? `${t('ticket.common.rates')} ${t('attendees.addAttendees.selectHierarchy.selectedPlural')} `
                      : `${t('ticket.common.rate')} ${t('attendees.addAttendees.selectHierarchy.selectedSingular')} `
              }`
            : '';
    }, []);

    const handleSubmit = React.useCallback(() => {
        form.fees.forEach(fee => {
            if (!fee.name) {
                fee.name = t('ticket.rates.ticket-management.swappable-config-rules.fee-name');
            }
        });

        let _newSwappableRateConfig = new SwappableRateConfig(form);
        _newSwappableRateConfig
            .save({
                organizationId: organization.id,
                eventId: event ? event.id : null,
                rateId: rate.id,
                id: configToEdit?.id
            })
            .then(() => {
                toast.success(t('ticket.rates.ticket-management.swappable-config-rules.toast-success'));
            })
            .catch(error => {
                console.error('Error:', error);
                toast.error(t('ticket.rates.ticket-management.swappable-config-rules.toast-error'));
            })
            .finally(() => {
                onClose();
                syncSwappableConfig();
            });
    }, [organization, event, rate, form, onClose, syncSwappableConfig, configToEdit]);

    if (!show) {
        return null;
    }

    return (
        <Modal
            size="large"
            allowExternalClosing={false}
            open={show}
            onClose={onClose}
            title={t('ticket.rates.ticket-management.create-modal.title')}
            subtitle={t('ticket.rates.ticket-management.create-modal.subtitle')}
            submitButton={
                <Button primary onClick={handleSubmit}>
                    {t('ticket.common.action.save')}
                </Button>
            }
            cancelButton={<Button onClick={onClose} label={t('ticket.common.cta.cancel')} />}
        >
            <Form>
                <Form.Field>
                    <Form.Label label={t('ticket.rates.ticket-management.create-modal.name-label')} />
                    <Form.Input
                        value={form.name}
                        placeholder={`${rate?.name} - ${t('ticket.rates.ticket-management.create-modal.name-placeholder')}`}
                        onChange={({ target: { value } }) => {
                            setForm('name', value);
                        }}
                    />
                    <Form.FieldErrors errors={formErrors['name'] ? [t(formErrors['name'])] : null} />
                </Form.Field>
                <Form.Field>
                    <SelectHierarchyLabel
                        label={
                            <span>
                                {t('ticket.rates.ticket-management.create-modal.rates-to-label')}
                                <TooltipHelp triggerOnHover title={t('ticket.rates.ticket-management.create-modal.rates-to-tooltip')} />
                            </span>
                        }
                        loading={loadingSwappableConfig || loadingRates}
                        placeholder={t('ticket.common.action.select')}
                        placeholderSearch={t('ticket.common.action.search')}
                        depth={0}
                        height="maxi"
                        labelSelectAll={t('attendees.addAttendees.selectHierarchy.label')}
                        selected={form.rates_to}
                        onChange={selected => setForm('rates_to', selected)}
                        onLabelSelected={handleLabelChange}
                        options={ratesOptions}
                        labelApply={t('ticket.common.action.select')}
                        multi
                    />
                </Form.Field>
                <Form.Field>
                    <ToggleLabel
                        inlinePosition="left"
                        label={t('ticket.rates.ticket-management.create-modal.pay_positive_difference-title')}
                        subtitle={t('ticket.rates.ticket-management.create-modal.pay_positive_difference-subtitle')}
                        checked={form.pay_positive_difference}
                        onChange={(e, { checked }) => setForm('pay_positive_difference', checked)}
                    />
                    <ToggleLabel
                        inlinePosition="left"
                        label={t('ticket.rates.ticket-management.create-modal.refund_positive_difference-title')}
                        subtitle={t('ticket.rates.ticket-management.create-modal.refund_positive_difference-subtitle')}
                        checked={form.refund_positive_difference}
                        onChange={(e, { checked }) => setForm('refund_positive_difference', checked)}
                    />
                    <ToggleLabel
                        inlinePosition="left"
                        label={t('ticket.rates.ticket-management.create-modal.display-fees-title')}
                        subtitle={t('ticket.rates.ticket-management.create-modal.display-fees-subtitle')}
                        checked={displayFees}
                        onChange={(e, { checked }) => {
                            setDisplayFees(currentState => !currentState);
                            if (!checked) {
                                setForm('fees', []);
                            }
                        }}
                    />
                </Form.Field>
                {displayFees && (
                    <Section>
                        <RateFeesEditor
                            fees={form.fees}
                            event={event}
                            onChange={value => setForm('fees', value)}
                            setForm={setForm}
                            titleKey="ticket.rates.ticket-management.create-modal.fees-title"
                        />
                    </Section>
                )}
            </Form>
        </Modal>
    );
}
